import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, faHome, faChartLine, faCalculator, 
  faTimes, faSync, faArrowRight, faBolt, faBuilding,
  faBed, faBath, faRuler, faCalendar
} from '@fortawesome/free-solid-svg-icons';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import axios from 'axios';
import axiosInstance from '../../../helpers/api';
import { useWorkflow } from '../WorkflowProvider';
import CompactPropertyCard from '../../modules/crm/CompactPropertyCard';
import PropertyDetailsInline from '../../modules/crm/PropertyDetailsInline';

// Mapbox access token will be set dynamically

interface PropertyData {
  address: string;
  bedrooms: number;
  bathrooms: number;
  living_area: number;
  year_built: number;
  city: string;
  postal_code: string;
  latitude?: number;
  longitude?: number;
  list_price?: number;
  close_price?: number;
  mls_number?: string;
  distance_miles?: number;
  similarity_score?: number;
  mls_status?: string;
  listing_office_name?: string;
  listing_agent_full_name?: string;
}

interface ComparableProperty extends PropertyData {
  // ARMLS format fields
  UnparsedAddress?: string;
  BedroomsTotal?: number;
  BathsTotal?: number;
  LivingArea?: number;
  YearBuilt?: number;
  ListPrice?: number;
  ClosePrice?: number;
  MlsNumber?: string;
  Latitude?: number;
  Longitude?: number;
  MlsStatus?: string;
  CloseDate?: string;
  ListingOfficeName?: string;
  ListingAgentFullName?: string;
  
  // ✅ MISSING FIELDS FROM PYTHON SCRIPT OUTPUT
  appraiser_score?: number;     // Exists in Python data
  final_score?: number;         // Exists in Python data  
  similarity_score?: number;    // Alternative scoring field
  distance_miles?: number;      // Distance from subject property
  close_date?: string;          // Alternative date field
  search_radius?: number;       // Search radius used
  match_type?: string;          // Match type (exact, etc)
  subdivision?: string;         // Subdivision name
  subdivision_tier?: number;    // Subdivision scoring tier
  subdivision_bonus?: number;   // Subdivision bonus points
  
  // Listing identifiers
  listing_id?: string;          // Listing ID from MLS
  mls_number?: string;          // MLS number (alternative)

  // ATTOM specific fields
  pricePerSqft?: number;        // Price per square foot from ATTOM
  priceVariance?: number;       // Price variance from ATTOM
  sqftVariance?: number;        // Square footage variance from ATTOM

  // KOPS-410 comp analysis fields
  classification?: string;      // Property classification (renovated, neutral, etc.)
  days_on_market?: number;      // Days on market
  pool_yn?: boolean;            // Has pool
  garage_spaces?: number;       // Number of garage spaces
  lot_size_sqft?: number;       // Lot size in square feet
  price_per_sqft?: number;      // Price per square foot
}

interface MarketStatistics {
  averagePrice: number;
  medianPrice: number;
  pricePerSqFt: number;
  averageDaysOnMarket: number;
  totalComparables: number;
  priceRange: { min: number; max: number };
  marketTrend: 'up' | 'down' | 'stable';
  trendPercentage: number;
}

interface Step2Props {
  onComplete: (analysisData: any) => void;
  onBack: () => void;
}

const Step2ProfessionalCMA: React.FC<Step2Props> = ({ onComplete, onBack }) => {
  console.log('🎯 [STEP2 PROFESSIONAL CMA] Component loaded!');
  
  // Component is loading correctly
  
  // Get workflow context
  const { state } = useWorkflow();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subjectProperty, setSubjectProperty] = useState<PropertyData | null>(null);
  const [comparables, setComparables] = useState<ComparableProperty[]>([]);
  const [selectedComparables, setSelectedComparables] = useState<Set<string>>(new Set());
  const [marketStats, setMarketStats] = useState<MarketStatistics | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'closed'>('all');
  const [sortBy, setSortBy] = useState<'distance' | 'price' | 'similarity'>('similarity');
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(new Set());
  const [propertyNotes, setPropertyNotes] = useState<Record<string, string>>({});
  const [compMLSData, setCompMLSData] = useState<Record<string, any>>({});
  const photosFetchedRef = useRef<Set<string>>(new Set());

  // Fetch MLS data + photos for ALL comps on load (primary photos show immediately)
  useEffect(() => {
    if (comparables.length === 0) return;

    const fetchAllCompPhotos = async () => {
      const listingIds = comparables
        .map(c => c.listing_id || c.mls_number)
        .filter(Boolean)
        .filter(id => !photosFetchedRef.current.has(id)); // Only fetch new ones
      if (listingIds.length === 0) return;

      listingIds.forEach(id => photosFetchedRef.current.add(id)); // Mark as fetching

      console.log(`📸 Fetching MLS photos for ${listingIds.length} comps...`);

      // Fetch in parallel (5 at a time to avoid rate limits)
      const batchSize = 5;
      for (let i = 0; i < listingIds.length; i += batchSize) {
        const batch = listingIds.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(async (listingId) => {
            try {
              const response = await axiosInstance.get(`/deals/mls/exact/${listingId}`);
              if (response.data.success && response.data.data) {
                return { listingId, data: response.data.data };
              }
            } catch (err) {
              // Skip failed fetches
            }
            return null;
          })
        );

        // Update state with each batch as it completes
        const newData: Record<string, any> = {};
        results.forEach(r => {
          if (r.status === 'fulfilled' && r.value) {
            newData[r.value.listingId] = r.value.data;
          }
        });

        if (Object.keys(newData).length > 0) {
          setCompMLSData(prev => ({ ...prev, ...newData }));
          // Update comp coordinates from MLS data (for map markers)
          setComparables(prev => prev.map(comp => {
            const key = comp.listing_id || comp.mls_number || '';
            const mls = newData[key];
            if (mls && mls.latitude && mls.longitude && !comp.latitude) {
              return { ...comp, latitude: mls.latitude, longitude: mls.longitude };
            }
            return comp;
          }));
          console.log(`📸 Loaded photos for ${Object.keys(newData).length} comps (batch ${Math.floor(i / batchSize) + 1})`);
        }
      }
    };

    fetchAllCompPhotos();
  }, [comparables]);

  // Mapbox refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  // Re-initialize map when comp coordinates become available from MLS fetch
  useEffect(() => {
    const compsWithCoords = comparables.filter(c => c.latitude && c.longitude);
    if (subjectProperty?.latitude && subjectProperty?.longitude && compsWithCoords.length > 0 && !map.current) {
      console.log(`🗺️ [MAP] Comp coordinates available (${compsWithCoords.length}), initializing map...`);
      initializeMap(subjectProperty, comparables as ComparableProperty[]).catch(err => {
        console.error('❌ [MAP] Failed to initialize map:', err);
      });
    }
  }, [comparables, subjectProperty]);

  useEffect(() => {
    loadCMAData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCMAData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🎯 [PROFESSIONAL CMA] Starting to load CMA data...');

      // First, check if we have pre-loaded seller report data (from Review & Edit Report)
      const sellerReportData = sessionStorage.getItem('sellerReportComps');
      if (sellerReportData) {
        console.log('✅ [PROFESSIONAL CMA] Using pre-loaded seller report data');
        const parsed = JSON.parse(sellerReportData);
        await processLoadedData(parsed);
        setLoading(false);
        return;
      }

      console.log('📊 [PROFESSIONAL CMA] Workflow state:', {
        hasPropertyInput: !!state.propertyInput,
        propertyAddress: state.propertyInput?.property_address,
        latitude: state.propertyInput?.latitude,
        longitude: state.propertyInput?.longitude
      });

      // Check if we have property data from the workflow context (Step 1)
      if (state.propertyInput && state.propertyInput.property_address) {
        console.log('✅ [PROFESSIONAL CMA] Using property data from workflow context');
        
        // Create subject property from workflow data
        const subjectFromWorkflow = {
          address: state.propertyInput.property_address,
          bedrooms: parseInt(state.propertyInput.bedrooms) || 0,
          bathrooms: parseFloat(state.propertyInput.bathrooms) || 0,
          living_area: parseInt(state.propertyInput.living_area) || 0,
          year_built: parseInt(state.propertyInput.year_built) || 0,
          latitude: state.propertyInput.latitude,
          longitude: state.propertyInput.longitude,
          list_price: parseFloat(state.propertyInput.list_price) || undefined,
          city: state.propertyInput.city || '',
          postal_code: state.propertyInput.postal_code || ''
        };

        // Process with this data
        await processLoadedData({
          subject_property: subjectFromWorkflow,
          comparable_properties: { comparables: [], total_found: 0 }
        });
        
        return; // Exit early, processLoadedData will fetch comps
      }

      // Fallback: Check sessionStorage
      const reportId = sessionStorage.getItem('currentReportId');
      const fullCMAResponse = sessionStorage.getItem('fullCMAResponse');

      // Check if Step 1 indicated to fetch from backend
      if (fullCMAResponse) {
        const parsed = JSON.parse(fullCMAResponse);
        if (parsed.fetch_from_backend && reportId) {
          console.log('🔄 [PROFESSIONAL CMA] Fetching full data from backend, report:', reportId);

          try {
            // FIXED: Use the correct Step 2 endpoint
            const response = await axiosInstance.get(`/smart-cma/step2/get-report/${reportId}`);

            console.log('🔍 [DEBUG] Backend response structure:', {
              success: response.data.success,
              hasData: !!response.data.data,
              dataKeys: response.data.data ? Object.keys(response.data.data) : [],
              propertyData: response.data.data?.propertyData,
              comparablesData: response.data.data?.comparablesData
            });

            if (response.data.success && response.data.data) {
              const reportData = response.data.data;

              console.log('🔍 [DEBUG] Backend response data keys:', Object.keys(reportData));
              console.log('🔍 [DEBUG] subject_property:', reportData.subject_property);
              console.log('🔍 [DEBUG] comparables_data:', reportData.comparables_data);

              // FIXED: Map backend structure correctly
              // The backend returns: data.subject_property and data.comparables_data
              const data = {
                report_id: reportId,
                subject_property: reportData.subject_property || reportData.propertyData,
                comparable_properties: reportData.comparables_data || reportData.comparablesData,
                analysis_data: reportData.analysis_data || reportData.analysisData,
                market_stats: reportData.market_stats
              };

              console.log('✅ [PROFESSIONAL CMA] Data fetched from backend:', {
                hasSubject: !!data.subject_property,
                subjectAddress: data.subject_property?.address,
                comparablesCount: data.comparable_properties?.total_found || data.comparable_properties?.comparables?.length || 0
              });

              // Process this data (skip the sessionStorage loop below)
              await processLoadedData(data);
              return;
            }
          } catch (fetchError) {
            console.error('❌ [PROFESSIONAL CMA] Failed to fetch from backend:', fetchError);
            throw new Error('Failed to load CMA data from server. Please try again.');
          }
        }
      }

      // Fallback: Try multiple sessionStorage keys (original logic)
      const sessionKeys = ['fullCMAResponse', 'smartCMAWorkflowData'];
      let data = null;

      for (const key of sessionKeys) {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);

            // Handle ATTOM API response structure
            if (parsed.success && parsed.data) {
              // This is the ATTOM response structure
              data = {
                subject_property: parsed.data.propertyAnalysis?.subjectProperty,
                comparable_properties: parsed.data.propertyAnalysis?.comparableProperties,
                price_prediction: parsed.data.propertyAnalysis?.valuationEstimate,
                market_insights: parsed.data.analysisInsights
              };
              console.log(`✅ [PROFESSIONAL CMA] Found ATTOM data in "${key}"`);
              break;
            } else if (key === 'smartCMAWorkflowData' && parsed.apiResponse) {
              // Legacy workflow data structure
              data = parsed.apiResponse;
              console.log(`✅ [PROFESSIONAL CMA] Found workflow data in "${key}"`);
              break;
            } else if (parsed.subject_property || parsed.comparable_properties) {
              // Direct format
              data = parsed;
              console.log(`✅ [PROFESSIONAL CMA] Found direct data in "${key}"`);
              break;
            }
          } catch (e) {
            console.log(`❌ [PROFESSIONAL CMA] Failed to parse "${key}":`, e);
          }
        }
      }

      if (!data) {
        throw new Error('No CMA data found. Please complete Step 1 first.');
      }

      await processLoadedData(data);
    } catch (error: any) {
      console.error('❌ [PROFESSIONAL CMA] Error loading data:', error);
      setError(error.message || 'Failed to load CMA data');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to process loaded data (extracted to avoid duplication)
  const processLoadedData = async (data: any) => {
    try {
      
      // Extract subject property
      const subject = data.subject_property;
      if (!subject) {
        throw new Error('Subject property data not found.');
      }
      
      const normalizedSubject: PropertyData = {
        address: subject.address || subject.UnparsedAddress || 'Unknown Address',
        bedrooms: subject.bedrooms || subject.BedroomsTotal || 0,
        bathrooms: subject.bathrooms || subject.BathsTotal || 0,
        living_area: subject.squareFeet || subject.living_area || subject.LivingArea || 0,
        year_built: subject.yearBuilt || subject.year_built || subject.YearBuilt || 0,
        city: subject.city || subject.City || '',
        postal_code: subject.postalCode || subject.postal_code || subject.PostalCode || '',
        latitude: subject.latitude || subject.Latitude,
        longitude: subject.longitude || subject.Longitude,
        list_price: subject.currentValue || subject.list_price || subject.ListPrice,
        mls_number: subject.mls_number || subject.MlsNumber,
        mls_status: subject.mls_status || subject.MlsStatus,
        listing_office_name: subject.listing_office_name || subject.ListingOfficeName,
        listing_agent_full_name: subject.listing_agent_full_name || subject.ListingAgentFullName
      };
      
      setSubjectProperty(normalizedSubject);
      
      // Extract comparable properties
      let comparableData = [];
      if (data.comparable_properties?.comparables) {
        comparableData = data.comparable_properties.comparables;
      } else if (data.comparable_properties && Array.isArray(data.comparable_properties)) {
        comparableData = data.comparable_properties;
      } else if (data.comparables && Array.isArray(data.comparables)) {
        comparableData = data.comparables;
      }
      
      console.log(`📊 [PROFESSIONAL CMA] Found ${comparableData.length} comparables`);

      // If no comparables found, try to fetch them from the backend
      if (comparableData.length === 0) {
        console.log('⚠️ [PROFESSIONAL CMA] No comparables in sessionStorage, checking backend...');

        const reportId = sessionStorage.getItem('currentReportId') || data.report_id;
        
        // First check if comparables are already in the database
        if (reportId) {
          try {
            console.log('🔍 [PROFESSIONAL CMA] Checking if comparables are already in database...');
            const reportResponse = await axiosInstance.get(`/smart-cma/${reportId}`);
            
            if (reportResponse.data.success && reportResponse.data.data.comparablesData) {
              const savedComparables = reportResponse.data.data.comparablesData;
              if (savedComparables && savedComparables.length > 0) {
                console.log(`✅ [PROFESSIONAL CMA] Found ${savedComparables.length} pre-computed comparables in database!`);
                comparableData = savedComparables;
                
                // Update sessionStorage with the fetched comparables
                data.comparable_properties = {
                  comparables: comparableData,
                  total_found: comparableData.length
                };
                sessionStorage.setItem('fullCMAResponse', JSON.stringify(data));
              }
            }
          } catch (dbError) {
            console.log('⚠️ [PROFESSIONAL CMA] Could not check database for comparables:', dbError.message);
          }
        }

        // If still no comparables, use the KOPS-410 Comp Analysis API
        if (comparableData.length === 0) {
          try {
          console.log('🔍 [PROFESSIONAL CMA] Using KOPS-410 comp analysis API');

          // Build request payload for comp analysis API
          const compRequest = {
            address: normalizedSubject.address,
            latitude: normalizedSubject.latitude,
            longitude: normalizedSubject.longitude,
            living_area: normalizedSubject.living_area,
            bedrooms: normalizedSubject.bedrooms,
            bathrooms: normalizedSubject.bathrooms,
            year_built: normalizedSubject.year_built,
            list_price: normalizedSubject.list_price,
            radius_miles: 1.0,
            months_back: 12,
            transaction_type: state.propertyInput?.transaction_type || 'sale'  // Include transaction type from Step 1
          };

          console.log('📤 [PROFESSIONAL CMA] Sending comp request:', compRequest);

          // Call the comp analysis endpoint through backend proxy
          console.log('🌐 [PROFESSIONAL CMA] Using backend proxy for comp analysis');
          console.log('🌐 [PROFESSIONAL CMA] Current location:', window.location.hostname, window.location.protocol);

          // Always use the backend proxy endpoint which handles CORS and protocol issues
          const fetchResponse = await axiosInstance.post('/comps/search', compRequest, {
            timeout: 30000  // 30 seconds timeout
          });

          const fetchData = fetchResponse.data;

          if (fetchData.success && fetchData.data && fetchData.data.comparables_data && fetchData.data.comparables_data.length > 0) {
            console.log(`✅ [PROFESSIONAL CMA] Fetched ${fetchData.data.comparables_data.length} comparables from KOPS-410 API`);
            comparableData = fetchData.data.comparables_data;

            // Update sessionStorage with the fetched comparables
            data.comparable_properties = {
              ...data.comparable_properties,
              comparables: comparableData,
              total_found: comparableData.length
            };

            // Also store market stats and valuation data
            data.market_stats = fetchData.data.market_stats;
            data.valuation = fetchData.data.valuation;

            sessionStorage.setItem('fullCMAResponse', JSON.stringify(data));

            console.log('📊 [PROFESSIONAL CMA] Market stats:', fetchData.data.market_stats);
            console.log('💰 [PROFESSIONAL CMA] Valuation:', fetchData.data.valuation);
          } else {
            setError('No comparable properties found for analysis. Please check the address and try again.');
            return;
          }
        } catch (fetchError: any) {
          console.error('❌ [PROFESSIONAL CMA] Failed to fetch comparables from KOPS-410 API:', fetchError);
          console.error('❌ [PROFESSIONAL CMA] Error details:', {
            message: fetchError.message,
            response: fetchError.response?.data,
            status: fetchError.response?.status,
            config: fetchError.config
          });

          // More specific error messages
          if (fetchError.code === 'ECONNABORTED') {
            setError('Request timed out while searching for comparable properties. The search area may be too broad or the server is busy. Please try again.');
          } else if (fetchError.response?.status === 401) {
            setError('Authentication failed. Please log in again.');
          } else if (fetchError.response?.status === 500) {
            setError('Server error while searching for comparables. Please try again in a moment.');
          } else {
            setError(`Failed to load comparable properties: ${fetchError.response?.data?.error || fetchError.message}. Please try again.`);
          }

          // Set some mock data so user can continue testing
          console.warn('⚠️ [PROFESSIONAL CMA] Setting mock comparables for testing purposes');
          const mockComparables = generateMockComparables(normalizedSubject);
          setComparables(mockComparables);
          setSelectedComparables(new Set(mockComparables.map(c => c.mls_number)));
          calculateMarketStatistics(mockComparables);
          return;
        }
        } // Close the inner if statement
      }
      
      // Normalize comparable properties to match expected format
      const normalizedComparables = comparableData.map((comp: any, index: number) => {
        // Debug log the raw data from KOPS-410 API
        if (index < 3) {
          console.log(`📊 [CMA DEBUG] Raw comparable ${index} from KOPS-410:`, {
            address: comp.address,
            close_price: comp.close_price,
            bedrooms: comp.bedrooms,
            bathrooms: comp.bathrooms,
            living_area: comp.living_area,
            similarity_score: comp.similarity_score,
            classification: comp.classification
          });
        }
        
        return {
          // Basic property info (KOPS-410 format)
          address: comp.address || '',
          bedrooms: comp.bedrooms || 0,
          bathrooms: comp.bathrooms || 0,
          living_area: comp.living_area || 0,
          year_built: comp.year_built || 0,
          
          // Pricing info (KOPS-410 returns close_price for sold properties)
          list_price: comp.list_price || comp.close_price || 0,
          close_price: comp.close_price || 0,
          
          // Location info
          city: comp.city || '',
          postal_code: comp.postal_code || '',
          latitude: comp.latitude,
          longitude: comp.longitude,
          distance_miles: comp.distance_miles || 0,
          
          // Analysis info
          listing_id: comp.listing_id || comp.mls_number || '',
          similarity_score: comp.similarity_score || 0,
          mls_number: comp.mls_number || comp.listing_id || '',
          mls_status: comp.mls_status || 'Closed',
          close_date: comp.close_date || '',
          
          // KOPS-410 specific fields
          classification: comp.classification || 'neutral',
          days_on_market: comp.days_on_market || 0,
          pool_yn: comp.pool_yn || false,
          garage_spaces: comp.garage_spaces || 0,
          lot_size_sqft: comp.lot_size_sqft || 0,
          pricePerSqft: comp.pricePerSqft || (comp.close_price && comp.living_area ? comp.close_price / comp.living_area : 0),
          
          // Legacy compatibility
          priceVariance: 0,
          sqftVariance: 0
        };
      });
      
      setComparables(normalizedComparables);
      
      // Debug log normalized data
      console.log('📊 [CMA DEBUG] First normalized comparable:', normalizedComparables[0]);
      
      // Select all comparables by default
      const allMlsNumbers = new Set<string>(
        normalizedComparables
          .map((comp: any) => String(comp.mls_number || ''))
          .filter(Boolean)
      );
      setSelectedComparables(allMlsNumbers);
      
      // Calculate market statistics
      calculateMarketStatistics(normalizedComparables);
      
      // Initialize map
      console.log('🗺️ [MAP] Subject property coordinates:', {
        lat: normalizedSubject.latitude,
        lng: normalizedSubject.longitude
      });
      
      console.log('🗺️ [MAP] Comparable coordinates:', normalizedComparables.map((comp, idx) => ({
        index: idx,
        address: comp.address,
        lat: comp.latitude,
        lng: comp.longitude
      })));
      
      if (normalizedSubject.latitude && normalizedSubject.longitude) {
        // Initialize map asynchronously
        initializeMap(normalizedSubject, normalizedComparables as ComparableProperty[]).catch(err => {
          console.error('❌ [MAP] Failed to initialize map:', err);
        });
      } else {
        console.warn('⚠️ [MAP] No coordinates for subject property, map will not be displayed');
      }

    } catch (err: any) {
      console.error('❌ [PROFESSIONAL CMA] Error processing data:', err);
      setError(err.message || 'Failed to process CMA data');
      throw err; // Re-throw to be caught by loadCMAData
    }
  };

  // Generate mock comparables for testing when API fails
  const generateMockComparables = (subject: PropertyData): ComparableProperty[] => {
    const mockData = [];
    const basePrice = subject.list_price || 450000;
    
    for (let i = 0; i < 10; i++) {
      const priceVariation = (Math.random() - 0.5) * 0.3; // +/- 15% variation
      const sqftVariation = (Math.random() - 0.5) * 0.2; // +/- 10% sqft variation
      const price = Math.round(basePrice * (1 + priceVariation));
      const sqft = Math.round((subject.living_area || 1500) * (1 + sqftVariation));
      
      mockData.push({
        address: `${1000 + i * 100} ${['Oak', 'Pine', 'Maple', 'Elm'][i % 4]} Street, ${subject.city || 'Tempe'}, AZ`,
        bedrooms: subject.bedrooms + Math.floor(Math.random() * 2 - 0.5),
        bathrooms: subject.bathrooms + (Math.random() > 0.5 ? 0 : 0.5),
        living_area: sqft,
        year_built: subject.year_built + Math.floor(Math.random() * 10 - 5),
        city: subject.city || 'Tempe',
        postal_code: subject.postal_code || '85281',
        latitude: (subject.latitude || 33.4484) + (Math.random() - 0.5) * 0.02,
        longitude: (subject.longitude || -111.9583) + (Math.random() - 0.5) * 0.02,
        list_price: price,
        close_price: price,
        mls_number: `MOCK${Date.now()}${i}`,
        distance_miles: (Math.random() * 0.8 + 0.1),
        similarity_score: (0.95 - i * 0.05),
        mls_status: 'Sold',
        listing_office_name: 'Mock Realty',
        listing_agent_full_name: 'Test Agent'
      });
    }
    
    return mockData;
  };

  const calculateMarketStatistics = (comparableData: ComparableProperty[]) => {
    if (comparableData.length === 0) return;
    
    // Filter out invalid prices and get valid comparables
    const validComparables = comparableData.filter(comp => {
      const price = comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price;
      const sqft = comp.LivingArea || comp.living_area;
      return price && price > 0 && sqft && sqft > 0;
    });
    
    if (validComparables.length === 0) return;
    
    // Calculate prices — ensure numeric values
    const prices = validComparables.map(comp =>
      Number(comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price || 0)
    ).filter(p => p > 0);

    const averagePrice = prices.length > 0 ? prices.reduce((sum, price) => sum + price, 0) / prices.length : 0;
    const sortedPrices = [...prices].sort((a, b) => a - b);
    const medianPrice = sortedPrices[Math.floor(sortedPrices.length / 2)];
    
    // Calculate price per sqft
    const pricePerSqFtValues = validComparables.map(comp => {
      const price = comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price || 0;
      const sqft = comp.LivingArea || comp.living_area || 1;
      return price / sqft;
    });
    const avgPricePerSqFt = pricePerSqFtValues.reduce((sum, val) => sum + val, 0) / pricePerSqFtValues.length;
    
    // Simple trend calculation (could be enhanced with time series analysis)
    const recentSales = validComparables.filter(comp => {
      const closeDate = comp.CloseDate || comp.close_date;
      if (!closeDate) return false;
      const saleDate = new Date(closeDate);
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      return saleDate > sixMonthsAgo;
    });
    
    let marketTrend: 'up' | 'down' | 'stable' = 'stable';
    let trendPercentage = 0;
    
    if (recentSales.length >= 2) {
      const recentAvg = recentSales.reduce((sum, comp) => 
        sum + (comp.ClosePrice || comp.ListPrice || 0), 0
      ) / recentSales.length;
      
      const difference = ((recentAvg - averagePrice) / averagePrice) * 100;
      trendPercentage = Math.abs(difference);
      
      if (difference > 2) marketTrend = 'up';
      else if (difference < -2) marketTrend = 'down';
    }
    
    const stats: MarketStatistics = {
      averagePrice: Math.round(averagePrice),
      medianPrice: Math.round(medianPrice),
      pricePerSqFt: Math.round(avgPricePerSqFt),
      averageDaysOnMarket: 30, // Could be calculated from actual DOM data
      totalComparables: validComparables.length,
      priceRange: {
        min: Math.min(...prices),
        max: Math.max(...prices)
      },
      marketTrend,
      trendPercentage: Math.round(trendPercentage)
    };
    
    setMarketStats(stats);
    console.log('📊 [MARKET STATS] Calculated:', stats);
  };

  const initializeMap = async (subject: PropertyData, comparableData: ComparableProperty[]) => {
    if (!mapContainer.current || !subject.latitude || !subject.longitude) return;
    
    try {
      // Get Mapbox token from backend - same as DealMap
      console.log('🗺️ [MAPBOX] Fetching Mapbox token...');
      try {
        const response = await axiosInstance.get('/properties/config/mapbox');
        const data = response.data;
        
        if (!data.mapboxToken) {
          console.error('❌ [MAPBOX] No Mapbox token available');
          return;
        }

        console.log('✅ [MAPBOX] Got Mapbox token, initializing map...');
        mapboxgl.accessToken = data.mapboxToken;
      } catch (err) {
        console.error('❌ [MAPBOX] Failed to fetch Mapbox token:', err);
        return;
      }
      
      // Initialize map centered on subject property - same style as DealMap
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        center: [subject.longitude, subject.latitude],
        zoom: 15,
        pitch: 45,
        bearing: -17.6
      });
      
      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl());
      map.current.addControl(new mapboxgl.FullscreenControl());
      map.current.addControl(new mapboxgl.ScaleControl());
      
      map.current.on('load', () => {
        if (!map.current) return;
        
        console.log('✅ [MAPBOX] Map loaded successfully');
        
        // Ensure proper sizing after style loads
        setTimeout(() => {
          if (map.current) {
            map.current.resize();
            console.log('✅ [MAPBOX] Map resized after load');
          }
        }, 300);
        
        // Add subject property marker (red like DealMap)
        new mapboxgl.Marker({
          color: '#dc3545' // Red for subject property
        })
        .setLngLat([subject.longitude!, subject.latitude!])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }).setHTML(`
            <div style="min-width: 200px;">
              <h6 class="mb-2 text-danger">📍 Subject Property</h6>
              <strong>${subject.address}</strong>
              <hr class="my-2">
              <div class="small">
                <div class="d-flex justify-content-between mb-1">
                  <span>List Price:</span>
                  <strong>$${subject.list_price?.toLocaleString() || 'N/A'}</strong>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <span>Bedrooms:</span>
                  <span>${subject.bedrooms}</span>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <span>Bathrooms:</span>
                  <span>${subject.bathrooms}</span>
                </div>
                <div class="d-flex justify-content-between">
                  <span>Living Area:</span>
                  <span>${subject.living_area?.toLocaleString()} sq ft</span>
                </div>
              </div>
            </div>
          `)
        )
        .addTo(map.current);
        
        // Add comparable property markers (green)
        comparableData.forEach((comp, index) => {
          const lat = comp.Latitude || comp.latitude;
          const lng = comp.Longitude || comp.longitude;
          
          if (lat && lng) {
            const price = comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price;
            const address = comp.UnparsedAddress || comp.address || 'Unknown Address';
            const bedrooms = comp.BedroomsTotal || comp.bedrooms || 0;
            const bathrooms = comp.BathsTotal || comp.bathrooms || 0;
            const sqft = comp.LivingArea || comp.living_area || 0;
            const mlsNumber = comp.MlsNumber || comp.mls_number || '';
            const status = comp.MlsStatus || comp.mls_status || '';

            new mapboxgl.Marker({ 
              color: '#28a745' // Green for comparables
            })
            .setLngLat([lng, lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(`
                <div style="min-width: 200px;">
                  <h6 class="mb-2 text-success">🏡 Comparable #${index + 1}</h6>
                  <strong>${address}</strong>
                  <hr class="my-2">
                  <div class="small">
                    <div class="d-flex justify-content-between mb-1">
                      <span>${status === 'Active' ? 'List Price' : 'Sold Price'}:</span>
                      <strong>$${price?.toLocaleString()}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-1">
                      <span>$/SqFt:</span>
                      <span>$${sqft ? Math.round(price / sqft) : 'N/A'}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-1">
                      <span>Beds/Baths:</span>
                      <span>${bedrooms} / ${bathrooms}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-1">
                      <span>Living Area:</span>
                      <span>${sqft?.toLocaleString()} sq ft</span>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span>MLS #:</span>
                      <span>${mlsNumber}</span>
                    </div>
                  </div>
                </div>
              `)
            )
            .addTo(map.current!);
          }
        });
        
        // Fit map to show all properties
        const coordinates: [number, number][] = [
          [subject.longitude!, subject.latitude!]
        ];
        
        comparableData.forEach(comp => {
          const lat = comp.Latitude || comp.latitude;
          const lng = comp.Longitude || comp.longitude;
          if (lat && lng) {
            coordinates.push([lng, lat]);
          }
        });
        
        if (coordinates.length > 1) {
          const bounds = coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
          
          map.current.fitBounds(bounds, { padding: 50 });
        }
      });
      
    } catch (error) {
      console.error('❌ [MAPBOX] Failed to initialize map:', error);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      default: return '➖';
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return 'success';
      case 'down': return 'danger';
      default: return 'secondary';
    }
  };

  const filteredComparables = comparables.filter(comp => {
    if (filterStatus === 'all') return true;
    const status = comp.MlsStatus || comp.mls_status || '';
    if (filterStatus === 'active') return status.toLowerCase().includes('active') || status.toLowerCase().includes('pending');
    if (filterStatus === 'closed') return status.toLowerCase().includes('closed');
    return true;
  });

  const sortedComparables = [...filteredComparables].sort((a, b) => {
    switch (sortBy) {
      case 'distance':
        return (a.distance_miles || 0) - (b.distance_miles || 0);
      case 'price':
        const priceA = a.ClosePrice || a.ListPrice || a.close_price || a.list_price || 0;
        const priceB = b.ClosePrice || b.ListPrice || b.close_price || b.list_price || 0;
        return priceB - priceA;
      case 'similarity':
      default:
        const scoreA = a.similarity_score || a.final_score || a.appraiser_score || 0;
        const scoreB = b.similarity_score || b.final_score || b.appraiser_score || 0;
        return scoreB - scoreA;
    }
  });

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <div 
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
            style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 0 30px rgba(0, 212, 255, 0.4)',
              animation: 'pulse 2s infinite'
            }}
          >
            <FontAwesomeIcon 
              icon={faBolt} 
              className="text-white" 
              size="2x"
            />
          </div>
          <h4 className="text-dark fw-bold mb-2">Loading Professional CMA Analysis</h4>
          <p className="text-muted">Preparing market intelligence and property mapping...</p>
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="text-center">
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          {error}
          <div className="mt-3">
            <Button variant="outline-danger" onClick={loadCMAData} className="me-2">
              <FontAwesomeIcon icon={faSync} className="me-1" />
              Retry
            </Button>
            <Button variant="outline-secondary" onClick={onBack}>
              ← Back to Step 1
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <div style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)', minHeight: '100vh' }}>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div 
              className="p-4 rounded-4"
              style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                border: '1px solid rgba(0, 212, 255, 0.2)'
              }}
            >
              <div className="d-flex align-items-center mb-3">
                <div 
                  className="d-flex align-items-center justify-content-center rounded-circle me-3"
                  style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                    boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
                  }}
                >
                  <FontAwesomeIcon icon={faChartLine} className="text-white" size="lg" />
                </div>
                <div>
                  <h2 className="mb-1 text-dark fw-bold">Automated CMA Analysis</h2>
                  <p className="mb-0 text-muted">AI-Powered Market Valuation</p>
                </div>
              </div>
              
              {subjectProperty && (
                <div className="d-flex align-items-center gap-4">
                  <Badge bg="primary" className="px-3 py-2">
                    <FontAwesomeIcon icon={faHome} className="me-1" />
                    {subjectProperty.address}
                  </Badge>
                  <Badge bg="success" className="px-3 py-2">
                    <FontAwesomeIcon icon={faBuilding} className="me-1" />
                    {comparables.length} Comparables
                  </Badge>
                  {marketStats && (
                    <Badge bg={getTrendColor(marketStats.marketTrend)} className="px-3 py-2">
                      {getTrendIcon(marketStats.marketTrend)} Market {marketStats.marketTrend.toUpperCase()}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Main Content */}
        <Row>
          {/* Map Column */}
          <Col lg={8}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-primary me-2" />
                  Property Locations
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <div 
                  ref={mapContainer} 
                  style={{ 
                    width: '100%', 
                    height: '500px',
                    borderRadius: '0 0 8px 8px'
                  }}
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Statistics Column */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon icon={faCalculator} className="text-success me-2" />
                  Market Statistics
                </h5>
              </Card.Header>
              <Card.Body>
                {marketStats ? (
                  <div className="space-y-3">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Average Price</span>
                      <strong className="text-success">{formatPrice(marketStats.averagePrice)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Median Price</span>
                      <strong>{formatPrice(marketStats.medianPrice)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Price per Sq Ft</span>
                      <strong>${marketStats.pricePerSqFt}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Price Range</span>
                      <small>{formatPrice(marketStats.priceRange.min)} - {formatPrice(marketStats.priceRange.max)}</small>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Market Trend</span>
                      <Badge bg={getTrendColor(marketStats.marketTrend)}>
                        {getTrendIcon(marketStats.marketTrend)} {marketStats.trendPercentage}%
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="text-muted">Total Comparables</span>
                      <strong className="text-primary">{marketStats.totalComparables}</strong>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    <Spinner size="sm" className="me-2" />
                    Calculating statistics...
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Subject Property Card */}
            {subjectProperty && (
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-transparent border-0">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
                    Subject Property
                  </h5>
                </Card.Header>
                <Card.Body>
                  <h6 className="text-primary mb-2">{subjectProperty.address}</h6>
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faBed} className="text-primary mb-1" />
                        <div className="fw-bold">{subjectProperty.bedrooms}</div>
                        <small className="text-muted">Beds</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faBath} className="text-info mb-1" />
                        <div className="fw-bold">{subjectProperty.bathrooms}</div>
                        <small className="text-muted">Baths</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faRuler} className="text-warning mb-1" />
                        <div className="fw-bold">{subjectProperty.living_area?.toLocaleString()}</div>
                        <small className="text-muted">Sq Ft</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faCalendar} className="text-secondary mb-1" />
                        <div className="fw-bold">{subjectProperty.year_built}</div>
                        <small className="text-muted">Built</small>
                      </div>
                    </div>
                  </div>
                  {subjectProperty.list_price && (
                    <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                      <h4 className="text-primary mb-0">{formatPrice(subjectProperty.list_price)}</h4>
                      <small className="text-muted">Current List Price</small>
                    </div>
                  )}
                  {subjectProperty.listing_office_name && (
                    <p className="mt-2 mb-0 text-muted small" style={{ fontStyle: 'italic' }}>
                      Courtesy of {subjectProperty.listing_office_name}
                      {subjectProperty.listing_agent_full_name && ` - ${subjectProperty.listing_agent_full_name}`}
                    </p>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* Comparables Table */}
        <Row className="mt-4">
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FontAwesomeIcon icon={faBuilding} className="text-success me-2" />
                    Comparable Properties ({sortedComparables.length})
                  </h5>
                  <div className="d-flex gap-2">
                    <Form.Select 
                      size="sm" 
                      value={filterStatus} 
                      onChange={(e) => setFilterStatus(e.target.value as any)}
                      style={{ width: 'auto' }}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active/Pending</option>
                      <option value="closed">Closed</option>
                    </Form.Select>
                    <Form.Select 
                      size="sm" 
                      value={sortBy} 
                      onChange={(e) => setSortBy(e.target.value as any)}
                      style={{ width: 'auto' }}
                    >
                      <option value="similarity">Sort by Match</option>
                      <option value="distance">Sort by Distance</option>
                      <option value="price">Sort by Price</option>
                    </Form.Select>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-3">
                <Row>
                  {sortedComparables.map((comp, index) => {
                    const propertyId = comp.listing_id || comp.mls_number || `comp-${index}`;
                    const isExpanded = expandedProperties.has(propertyId);
                    const similarity = comp.similarity_score || (comp.living_area && subjectProperty?.living_area
                      ? Math.max(0, Math.round(100 - Math.abs(comp.living_area - subjectProperty.living_area) / subjectProperty.living_area * 50))
                      : 85);
                    const rawDate = comp.close_date;
                    const saleDate = rawDate
                      ? (typeof rawDate === 'string' ? new Date(rawDate).toLocaleDateString() : '')
                      : '';

                    // Map comp data to property card format
                    const listingKey = comp.listing_id || comp.mls_number || '';
                    const mlsEnriched = compMLSData[listingKey];
                    const images = mlsEnriched?.images || [];
                    // Parse city/state/zip from full address if not separate fields
                    const addressParts = (comp.address || '').split(',').map((s: string) => s.trim());
                    const parsedCity = comp.city || (addressParts.length >= 2 ? addressParts[1] : '');
                    const stateZipPart = addressParts.length >= 3 ? addressParts[2] : '';
                    const stateMatch = stateZipPart.match(/([A-Z]{2})\s*(\d{5})?/);

                    const propertyForCard = {
                      ...comp,
                      images: images.length > 0 ? images : undefined,
                      price: comp.close_price || comp.list_price || 0,
                      address: comp.address || 'Unknown Address',
                      city: parsedCity,
                      state: stateMatch ? stateMatch[1] : '',
                      zip: stateMatch && stateMatch[2] ? stateMatch[2] : comp.postal_code || '',
                      mls_number: comp.listing_id || comp.mls_number || '',
                      listing_office_name: mlsEnriched?.listing_office_name || mlsEnriched?.listing_agent?.brokerage || '',
                      listing_agent_full_name: mlsEnriched?.listing_agent?.name || (typeof mlsEnriched?.listing_agent === 'string' ? mlsEnriched.listing_agent : '') || '',
                      listingBrokerage: mlsEnriched?.listing_office_name || mlsEnriched?.listing_agent?.brokerage || '',
                      listingAgent: mlsEnriched?.listing_agent?.name || '',
                      bedrooms: comp.bedrooms || 0,
                      bathrooms: comp.bathrooms || 0,
                      squareFeet: comp.living_area || 0,
                      living_area: comp.living_area || 0,
                      year_built: comp.year_built || 0,
                      matchScore: similarity,
                      description: `Sold ${saleDate} | ${comp.distance_miles ? comp.distance_miles + ' mi from subject' : ''} | ${comp.classification === 'renovated' ? 'Renovated' : 'Original condition'} | $${comp.price_per_sqft || Math.round((comp.close_price || 0) / (comp.living_area || 1))}/sqft | ${comp.days_on_market || 0} days on market`
                    };

                    // Auto-generate CMA notes
                    const autoNotes = propertyNotes[propertyId] || `${comp.classification === 'renovated' ? 'Renovated property' : 'Original condition'} - Sold ${saleDate} for $${(comp.close_price || 0).toLocaleString()} ($${comp.price_per_sqft || Math.round((comp.close_price || 0) / (comp.living_area || 1))}/sqft). ${comp.distance_miles ? comp.distance_miles + ' miles from subject.' : ''} ${comp.days_on_market ? comp.days_on_market + ' DOM.' : ''}`;

                    return (
                      <Col lg={12} className="mb-3" key={propertyId}>
                        <div className="position-relative">
                          {similarity >= 90 && !isExpanded && (
                            <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 1 }}>
                              <Badge bg="success">Top Match</Badge>
                            </div>
                          )}
                          <Badge
                            bg={comp.classification === 'renovated' ? 'success' : 'secondary'}
                            className="position-absolute top-0 start-0 m-2"
                            style={{ zIndex: 1 }}
                          >
                            {comp.classification === 'renovated' ? 'Renovated' : 'Original'}
                          </Badge>

                          <CompactPropertyCard
                            property={propertyForCard}
                            onView={() => {
                              const next = new Set(expandedProperties);
                              if (next.has(propertyId)) next.delete(propertyId); else next.add(propertyId);
                              setExpandedProperties(next);
                            }}
                            matchScore={similarity}
                            showDescription={true}
                          />

                          {isExpanded && (
                            <PropertyDetailsInline
                              property={mlsEnriched ? {
                                ...propertyForCard,
                                images: mlsEnriched.images || propertyForCard.images,
                                description: mlsEnriched.public_remarks || propertyForCard.description,
                                latitude: mlsEnriched.latitude || comp.latitude,
                                longitude: mlsEnriched.longitude || comp.longitude,
                                listingAgent: mlsEnriched.listing_agent,
                                subdivision: mlsEnriched.subdivision_name,
                                schools: mlsEnriched.elementary_school ? {
                                  elementary: mlsEnriched.elementary_school,
                                  middle: mlsEnriched.middle_school,
                                  high: mlsEnriched.high_school
                                } : undefined
                              } : propertyForCard}
                              isExpanded={isExpanded}
                              onToggleExpand={() => {
                                const next = new Set(expandedProperties);
                                next.delete(propertyId);
                                setExpandedProperties(next);
                              }}
                              agentNotes={autoNotes}
                              onSaveNotes={(notes) => setPropertyNotes(prev => ({ ...prev, [propertyId]: notes }))}
                              leadName="Seller"
                              matchScore={similarity}
                              aiReasoning={{
                                primary: `${comp.classification === 'renovated' ? 'Renovated' : 'Original condition'} comparable ${comp.distance_miles ? comp.distance_miles + ' mi away' : 'nearby'}`,
                                factors: [
                                  `Sold for $${(comp.close_price || 0).toLocaleString()} (${saleDate})`,
                                  `${comp.bedrooms}bd/${comp.bathrooms}ba, ${(comp.living_area || 0).toLocaleString()} sqft`,
                                  `$${comp.price_per_sqft || Math.round((comp.close_price || 0) / (comp.living_area || 1))}/sqft`,
                                  comp.days_on_market ? `${comp.days_on_market} days on market` : ''
                                ].filter(Boolean)
                              }}
                            />
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row className="mt-4">
          <Col className="text-center">
            <div className="d-flex gap-3 justify-content-center">
              <Button 
                variant="outline-secondary" 
                size="lg"
                onClick={onBack}
                className="px-4"
              >
                ← Back
              </Button>
              
              <Button 
                variant="primary"
                size="lg"
                onClick={() => {
                  // Create complete analysis data for Step 3 with real data only
                  const analysisData = {
                    success: true,
                    subject_property: subjectProperty,
                    comparable_properties: {
                      comparables: sortedComparables
                    },
                    marketStats,
                    selectedComparables: Array.from(selectedComparables),
                    // Real comparable statistics from our analysis
                    comparable_stats: {
                      count: sortedComparables.length,
                      price_min: marketStats?.priceRange.min,
                      price_max: marketStats?.priceRange.max,
                      price_avg: marketStats?.averagePrice
                    }
                  };
                  
                  // Save to sessionStorage for Step 3 to read
                  sessionStorage.setItem('fullCMAResponse', JSON.stringify(analysisData));
                  
                  // Call the onComplete callback
                  onComplete(analysisData);
                }}
                className="px-5"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)'
                }}
              >
                <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                Continue to AI Price Prediction
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Step2ProfessionalCMA;